import { appMessage as message } from "../utils/appMessage";
import { createContext, useContext, useEffect, useState } from "react";
import apiClient, {
  clearPersistedAuthSession,
  isNetworkFailure,
  markPersistedAuthSession,
  refreshAccessToken,
} from "../utils/apiClient";
import { isTokenExpired } from "../utils/tokenExpiry";
import i18n from "../i18n";
import { useAppConfig } from "./useAppConfig";
import { useTokenVerification } from "./useTokenVerification";
import { signup } from "./authRequests";

import Cookies from "universal-cookie";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const cookies = new Cookies(null, { path: "/" });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => cookies.get("token"));
  const [demoRole, setDemoRole] = useState(
    () => localStorage.getItem("demoRole") || null,
  );

  const appConfig = useAppConfig();

  useTokenVerification({
    cookies,
    token,
    setToken,
    demoRole,
    appConfig,
    setUser,
    setLoading,
  });

  // Refresh when the user returns to the tab after the access token may have
  // expired (e.g. laptop lid closed, tab in background for > 15 minutes).
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const currentToken = cookies.get("token");
      if (isTokenExpired(currentToken)) {
        try {
          const newToken = await refreshAccessToken();
          setToken(newToken);
        } catch (err) {
          // Local-only: logout() would POST /api/auth/logout, which revokes
          // every session of the user on all devices.
          if (!isNetworkFailure(err)) clearLocalSession();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [!!user]);

  const login = async (emailOrUsername, password, company, cookieConsent) => {
    try {
      const { data } = await apiClient.post("/api/auth/login", {
        emailOrUsername,
        password,
        company,
        cookieConsent,
      });

      if (!data.ok) {
        throw new Error(data.error || "Login failed");
      }

      cookies.set("token", data.token, {
        path: "/",
        maxAge: 7 * 24 * 60 * 60,
      });
      cookies.set("refresh", data.refreshToken, {
        path: "/",
        maxAge: 7 * 24 * 60 * 60,
      });

      markPersistedAuthSession();
      setToken(data.token);
      const user = data.user;

      user.actualRole = user.role || "user";

      // Use demo role if set, otherwise use actual role
      user.role = demoRole || user.actualRole;
      setUser(user);
      message.success(i18n.t("auth.login.messages.loginSuccess"));
      return { success: true };
    } catch (err) {
      const responseData = err?.response?.data;
      return {
        success: false,
        error: responseData?.error || err.message,
        i18nKey: responseData?.i18nKey || null,
      };
    }
  };


  function clearLocalSession() {
    cookies.remove("token");
    cookies.remove("refresh");
    localStorage.removeItem("demoRole");
    clearPersistedAuthSession();
    setToken(null);
    setUser(null);
    setDemoRole(null);
    message.info(i18n.t("auth.logout.messages.loggedOut"));
  }

  const logout = async () => {
    try {
      if (token) await apiClient.post("/api/auth/logout");
    } catch {
      // Server-side revoke failed (interceptor already reports HTTP errors);
      // the local session is cleared regardless, so callers never see a rejection.
    } finally {
      clearLocalSession();
    }
  };

  const switchDemoRole = (role) => {
    if (!user) {
      message.warning(i18n.t("auth.login.messages.loginRequired"));
      return;
    }

    const allowedRoles = ["admin", "official", "user"];
    if (role && !allowedRoles.includes(role)) {
      message.error(i18n.t("auth.login.messages.invalidRole"));
      return;
    }

    if (role) {
      localStorage.setItem("demoRole", role);
      setDemoRole(role);
      message.success(i18n.t("auth.login.messages.demoRoleSwitched", { role }));
    } else {
      localStorage.removeItem("demoRole");
      setDemoRole(null);
      message.info(i18n.t("auth.login.messages.demoRoleReset"));
    }
  };

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        isAuthenticated,
        demoRole,
        appConfig,
        login,
        signup,
        logout,
        switchDemoRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
